import { useMediaState } from '@vidstack/react';
import { PlayIcon } from '@vidstack/react/icons';
import React, { useEffect, useState } from 'react';
import { useVideoContext } from '../VideoContextManager/VideoContextManager';

interface FullscreenPlayControlProps {
    hasInteracted: boolean;
}

// Vidstack loops in script — it does NOT set the native `loop` attribute. At every
// loop boundary the underlying <video> hits end-of-media and fires a native `pause`
// (-> paused === true) before vidstack seeks back to 0 and replays ~10ms later
// (setTimeout(…,10) + rAF). Revealing the center play affordance the instant `paused`
// flips true makes it flash once per loop on an autoplaying clip the user has touched.
// Only reveal it once `paused` has settled past that transient window.
const SETTLED_PAUSE_MS = 200;

const FullscreenPlayControl: React.FC<FullscreenPlayControlProps> = ({ hasInteracted }) => {
    const { playerRef } = useVideoContext();
    const paused = useMediaState('paused', playerRef);
    const isControlsVisible = useMediaState('controls', playerRef);
    const canPlay = useMediaState('canPlay', playerRef);

    // Debounce the *appearance* of the play icon so the end-of-loop pause blip never
    // flashes it; hide immediately the moment playback resumes.
    const [isPauseSettled, setIsPauseSettled] = useState(false);
    useEffect(() => {
        if (!paused) {
            setIsPauseSettled(false);
            return;
        }
        const timeout = setTimeout(() => setIsPauseSettled(true), SETTLED_PAUSE_MS);
        return () => clearTimeout(timeout);
    }, [paused]);

    const handleVideoClick = (e: React.MouseEvent) => {
        // Get the click target and check if it's part of the controls
        const target = e.target as HTMLElement;
        const isControlsClick = target.closest('[class*="vds-controls"]');
        const isSettingsClick = target.closest('[class*="vds-menu"]');

        // Only handle play/pause if we didn't click the controls or settings
        if (!isControlsClick && !isSettingsClick && playerRef.current && canPlay) {
            if (paused) {
                playerRef.current.play();
            } else {
                playerRef.current.pause();
            }
        }
    };

    if (!hasInteracted) {
        return (
            <div
                className="absolute inset-0 w-full h-full cursor-pointer"
                onClick={handleVideoClick}
                aria-label="Play video"
            />
        );
    }

    return (
        <div
            className="absolute inset-0 w-full h-full cursor-pointer"
            onClick={handleVideoClick}
            aria-label={paused ? "Play video" : "Pause video"}
        >
            {/* Show play icon only once paused has settled (ignoring the loop blip) and controls aren't visible */}
            {isPauseSettled && !isControlsVisible && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="p-4 rounded-full transition-opacity">
                        <PlayIcon className="w-24 h-12 text-white" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FullscreenPlayControl;